import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { PinType, FetchParamsSubscribe } from 'types/ApiTypes';
import { OrderSubscription, Service, SubscribeData, SubscribeOption } from 'types/Entitlement';
import { PinFlowType, usePin } from 'providers/usePin/PinContext';
import { UpsellOptionSelector } from 'components/UpsellOptionSelector/UpsellOptionSelector';
import { UpsellMessageScreen } from 'components/UpsellMessageSelector/UpsellMessageSelector';
import GSApi from 'api/Api';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { useChannels } from 'providers/useChannels/useChannels';
import { useApp } from 'providers/useApp/AppContext';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import translate from 'utils/fnTranslate';
import { SubscriptionConfirmation } from 'components/SubscriptionConfirmation/SubscriptionConfirmation';
import { getOfferTypeByTag } from 'utils/fnEntitlement';
import { useServiceActivatedNotification } from 'components/Notification/Notification';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import Events from 'utils/Events';
import { setPurchaseSynced } from 'providers/reducers/purchase-synced-reducer';
import { SERVICE_ACTIVATED_EVENT } from 'hooks/useServiceActivationWatcher/useServiceActivationWatcher';
import { useModalRoot } from 'hooks/usePortalRoot/usePortalRoot';
import { useAuth } from 'providers/useAuth/AuthContext';
import { SECONDS } from 'utils/TimeUnit';
import { SubscriptionValidator } from './SubscriptionValidator';
import { SubscriptionValidatorInfo } from './SubscriptionPackageValidator';

enum FlowStep {
    UPSELL_MESSAGE = 'upsell_message',
    SELECT_OFFER = 'select_offer',
    CONFIRM_OFFER = 'confirm_offer',
    ENTER_PIN = 'enter_pin',
    PROCESSING = 'processing',
    ACTIVATION_DELAYED = 'activation_delayed',
    ERROR = 'subscribe_error',
}

const ACTIVATION_DELAY_TIMEOUT = SECONDS.toMillis(60);

type PendingActivation = {
    cacheKey: string;
    activate: () => void;
};

export type SubscriptionCallback = {
    onSuccess: () => void;
    showUpsellMessage?: boolean;
    upsellMessage?: string;
    channelLogo?: string;
    channelName?: string;
    onCancel?: () => void;
};

export const SUBSCRIPTION_CHANGED_EVENT = 'SUBSCRIPTION_CHANGED';

const useSubscribeService = () => {
    const [subscribeData, setSubscribeData] = useState<SubscribeData>(null);
    const [selectedOffer, setSelectedOffer] = useState<SubscribeOption>(null);
    const [step, setStep] = useState<FlowStep>(null);
    const [subscriptionCallback, setSubscriptionCallback] = useState<SubscriptionCallback>(null);
    const [pendingOffer, setPendingOffer] = useState<SubscribeOption | undefined>();
    const [skippedSelectOffer, setSkippedSelectOffer] = useState(false);
    const tvPostActionEnabled = useRef(false);
    const events = useRef(new Events());
    const dispatch = useDispatch();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { fetchChannels } = useChannels();
    const { appLanguage } = useApp();
    const { cacheKey } = useAuth();

    const showActivatedNotification = useServiceActivatedNotification();
    const subscriptionValidator = useRef<SubscriptionValidator>(null);
    const pendingActivation = useRef<PendingActivation>(null);

    const {
        response: subscriptionOffers,
        error: subscriptionOfferError,
        loading: subscriptionOffersLoading,
        reset: resetSubscriptionOffers,
        fetcher: fetchSubscriptionOffers,
        hasLoaded: subscriptionOffersLoaded,
    } = useDataFetcher<Service[], SubscribeData>(
        (data) => {
            const base =
                data?.type === 'Channel'
                    ? { channel_id: data?.code, lang: appLanguage }
                    : data?.type === 'Listing'
                      ? { listing_id: data?.code, lang: appLanguage }
                      : { vod_asset_id: data?.code, lang: appLanguage };
            const params = data?.capability ? { ...base, capability: data.capability } : base;
            return GSApi.getServices(params);
        },
        onNoInternet,
        onNetworkError
    );

    const { openPinDialog, closePinDialog, validatedSubscriptionPin } = usePin();

    const {
        response: orderResponse,
        error: orderError,
        loading: isOrdering,
        reset: resetOrder,
        fetcher: orderProduct,
        responseCode: orderResponseCode,
    } = useDataFetcher<OrderSubscription, FetchParamsSubscribe>(
        (props) => GSApi.createServicePurchase(props.productId, props.purchasePin),
        onNoInternet,
        onNetworkError
    );

    const initSubscribeFlow = (playerUrl: string, toSubscribe: SubscribeData, callback?: SubscriptionCallback) => {
        setSubscribeData(toSubscribe);
        setSubscriptionCallback(callback);

        if (callback?.showUpsellMessage) {
            setStep(FlowStep.UPSELL_MESSAGE);
        } else {
            setStep(FlowStep.SELECT_OFFER);
        }
    };

    const goToSubscriptionConfirmation = () => {
        setStep(FlowStep.CONFIRM_OFFER);
    };

    const subscribe = (externalOffer?: SubscribeOption) => {
        setPendingOffer(externalOffer);
        openPinDialog(PinFlowType.CHECK, PinType.RENT);
    };

    const purchaseSubscription = async (offer: SubscribeOption, pin: string) => {
        if (offer) {
            subscriptionValidator.current?.addValidator({
                type: 'Product',
                code: offer.packageId,
                assetId: offer.assetId,
            });
        }

        return GSApi.createServicePurchase(offer.productId, pin);
    };

    const exitFlow = () => {
        pendingActivation.current = null;
        setSubscribeData(null);
        setSelectedOffer(null);
        setStep(null);
        setSubscriptionCallback(null);
        resetSubscriptionOffers();
        resetOrder();
        closePinDialog(false);
        tvPostActionEnabled.current = false;
        setSkippedSelectOffer(false);
    };

    const onPinDialogClosed = () => {
        if (subscriptionCallback?.onCancel) {
            subscriptionCallback.onCancel();
        }
        exitFlow();
    };

    const onPinValidated = () => {
        subscribe();
    };

    const goToEnterPin = () => {
        setStep(FlowStep.ENTER_PIN);
        openPinDialog(PinFlowType.CHECK, PinType.RENT, {
            onPinValidated,
            onDialogClosed: onPinDialogClosed,
            onBack: () => setStep(FlowStep.CONFIRM_OFFER),
            canGoBack: true,
        });
    };

    useEffect(() => {
        if (validatedSubscriptionPin) {
            if (pendingOffer) {
                subscriptionValidator.current.addValidator({
                    type: 'Product',
                    code: pendingOffer?.packageId,
                    assetId: pendingOffer?.assetId,
                });
            }

            orderProduct({
                productId: (pendingOffer ?? selectedOffer).productId,
                purchasePin: validatedSubscriptionPin,
            });

            setPendingOffer(undefined);
            closePinDialog(false);
            setStep(FlowStep.PROCESSING);
        }
    }, [validatedSubscriptionPin]);

    const onSubscribeSuccessful = () => {
        if (subscriptionCallback) {
            subscriptionCallback.onSuccess();
        }

        dispatch(setPurchaseSynced());

        let activated = false;
        let delayTimer: number = null;
        const activation: PendingActivation = { cacheKey, activate: null };
        const onActivated = () => {
            if (activated) return;
            activated = true;
            window.clearTimeout(delayTimer);
            if (subscribeData) {
                subscriptionValidator.current?.addValidator(subscribeData);
            }
            fetchChannels(true).finally(() => {
                if (pendingActivation.current === activation) {
                    exitFlow();
                }

                showActivatedNotification();
            });
        };

        const onServiceActivated = (event: Event) => {
            event.preventDefault();
            onActivated();
        };

        activation.activate = onActivated;
        pendingActivation.current = activation;
        window.addEventListener(SERVICE_ACTIVATED_EVENT, onServiceActivated, { once: true });
        delayTimer = window.setTimeout(() => {
            if (pendingActivation.current === activation) {
                setStep(FlowStep.ACTIVATION_DELAYED);
            }
        }, ACTIVATION_DELAY_TIMEOUT);
    };

    useEffect(() => {
        const activation = pendingActivation.current;

        if (activation && cacheKey && cacheKey !== activation.cacheKey) {
            activation.activate();
        }
    }, [cacheKey]);

    const addSubscriptionChangedListener = (callback: (data: SubscriptionValidatorInfo) => void) => {
        events.current.addEventListener(SUBSCRIPTION_CHANGED_EVENT, callback);
    };

    const removeSubscriptionChangedListener = (callback: (data: SubscriptionValidatorInfo) => void) => {
        events.current.removeEventListener(SUBSCRIPTION_CHANGED_EVENT, callback);
    };

    useEffect(() => {
        if (step && step === FlowStep.SELECT_OFFER) {
            setSelectedOffer(null);
        }

        if (step && (step === FlowStep.SELECT_OFFER || step === FlowStep.CONFIRM_OFFER)) {
            closePinDialog(false);
        }
    }, [step]);

    useEffect(() => {
        if (subscribeData) {
            fetchSubscriptionOffers(subscribeData);
        }
    }, [subscribeData]);

    const serviceToSubscribeOption = (service: Service): SubscribeOption => ({
        name: service.name,
        tags: [service.type],
        price: service.price.value,
        productId: service.id,
        poster: service.image_by_type?.poster || subscribeData?.image || '',
        description: service.description,
    });

    useEffect(() => {
        if (subscriptionOffers && step) {
            if (subscriptionOffers.length === 1) {
                setSelectedOffer(serviceToSubscribeOption(subscriptionOffers[0]));
            }
        }
    }, [subscriptionOffers]);

    useEffect(() => {
        if (selectedOffer) {
            goToSubscriptionConfirmation();
        }
    }, [selectedOffer]);

    useEffect(() => {
        // execute only if the subscription context initialized the flow
        if (orderResponseCode === 200 && step) {
            onSubscribeSuccessful();
            resetOrder();
        }
    }, [orderResponseCode]);

    useEffect(() => {
        if (step && orderError) {
            setStep(FlowStep.ERROR);
        }
    }, [orderError]);

    return {
        pendingFlow: subscribeData != null,
        step,
        setStep,
        subscribeData,
        initSubscribeFlow,
        cancel: () => {
            exitFlow();
        },
        goBack: () => {
            if (step === FlowStep.CONFIRM_OFFER) {
                setStep(FlowStep.SELECT_OFFER);
            } else if (step === FlowStep.SELECT_OFFER && subscriptionCallback?.showUpsellMessage) {
                setStep(FlowStep.UPSELL_MESSAGE);
            }
        },
        canGoBack:
            (step === FlowStep.CONFIRM_OFFER && !skippedSelectOffer) ||
            (step === FlowStep.SELECT_OFFER && subscriptionCallback?.showUpsellMessage),
        skippedSelectOffer,
        setSkippedSelectOffer,
        onSubscribeSuccessful,
        fetchSubscriptionOffers,
        subscriptionOffersLoading,
        subscriptionOffers,
        subscriptionOfferError,
        subscriptionOffersLoaded,
        resetSubscriptionOffers,

        selectOffer: (subscribeOption: SubscribeOption) => {
            setSelectedOffer(subscribeOption);
        },
        selectedOffer,
        subscriptionCallback,

        subscribe,
        purchaseSubscription,
        orderResponse,
        isOrdering,
        orderError,
        resetOrder,
        goToEnterPin,

        addSubscriptionChangedListener,
        removeSubscriptionChangedListener,
    };
};

export const SubscriptionContext = createContext<ReturnType<typeof useSubscribeService>>(null);

export const SubscribeProvider = ({ children }) => {
    const service = useSubscribeService();

    return <SubscriptionContext.Provider value={service}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => useContext(SubscriptionContext);

export const useSubscribeFlowScreenProvider = () => {
    const {
        pendingFlow,
        step,
        cancel,
        goBack,
        canGoBack,
        subscriptionOffersLoading,
        subscriptionOffers,
        subscriptionOfferError,
        selectOffer,
        selectedOffer,
        orderError,
        isOrdering,
        goToEnterPin,
        subscriptionCallback,
        setStep,
        setSkippedSelectOffer,
        subscribeData,
    } = useSubscription();
    const modalRoot = useModalRoot();

    const getLoadingScreen = () => createPortal(<LoadingIndicator overlay={true} />, modalRoot);

    const getActivationScreen = () =>
        createPortal(<LoadingIndicator overlay={true} loaderHint={translate('SERVICE_ACTIVATION_IN_PROGRESS_TEXT')} />, modalRoot);

    const getActivationDelayedAlert = () => (
        <AlertDialog
            transparent
            title={translate('SERVICE_ACTIVATION_DELAY_NOTIFICATION_TITLE')}
            bodyText={translate('SERVICE_ACTIVATION_DELAY_NOTIFICATION_BODY')}
            buttons={[
                {
                    text: translate('CLOSE_BUTTON'),
                    onClick: cancel,
                },
            ]}
        />
    );

    const getTransformedOffers = (): SubscribeOption[] =>
        (Array.isArray(subscriptionOffers) ? subscriptionOffers : []).map((service: Service) => ({
            name: service.name,
            tags: [service.type],
            price: service.price.value,
            productId: service.id,
            poster: service.image_by_type?.poster || subscribeData?.image || '',
            description: service.description,
        }));

    // Skip SELECT_OFFER step when there's only 1 offer
    useEffect(() => {
        if (step === FlowStep.SELECT_OFFER && !subscriptionOffersLoading && subscriptionOffers) {
            const transformedOffers = getTransformedOffers();
            if (transformedOffers.length === 1) {
                selectOffer(transformedOffers[0]);
                setSkippedSelectOffer(true);
                setStep(FlowStep.CONFIRM_OFFER);
            }
        }
    }, [step, subscriptionOffersLoading, subscriptionOffers]);

    const handleUpsellContinue = () => {
        const transformedOffers = getTransformedOffers();

        if (transformedOffers.length === 1) {
            selectOffer(transformedOffers[0]);
            setStep(FlowStep.CONFIRM_OFFER);
        } else {
            setStep(FlowStep.SELECT_OFFER);
        }
    };

    const getUpsellMessageScreen = () => (
        <UpsellMessageScreen
            message={subscriptionCallback?.upsellMessage}
            channelLogo={subscriptionCallback?.channelLogo}
            channelName={subscriptionCallback?.channelName}
            onContinue={handleUpsellContinue}
            onClose={() => {
                if (subscriptionCallback?.onCancel) {
                    subscriptionCallback.onCancel();
                }
                cancel();
            }}
        />
    );

    const getSelectOfferScreen = () => {
        if (!step) {
            return null;
        }

        if ((!subscriptionOfferError && subscriptionOffersLoading) || !subscriptionOffers) {
            return getLoadingScreen();
        }

        // Transform the API response to match SubscribeOption type
        const transformedOffers = getTransformedOffers();

        if (transformedOffers.length === 1) {
            return getLoadingScreen();
        }

        if (transformedOffers.length === 0) {
            return (
                <AlertDialog
                    title={translate('NOTIFICATION_NO_PRODUCTS_TITLE')}
                    bodyText={translate('NOTIFICATION_NO_PRODUCTS_BODY')}
                    buttons={[
                        {
                            text: translate('CLOSE_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        return (
            <UpsellOptionSelector
                onClose={() => {
                    if (subscriptionCallback?.onCancel) {
                        subscriptionCallback.onCancel();
                    }
                    cancel();
                }}
                onBack={goBack}
                options={transformedOffers.map((offer) => ({
                    id: offer.productId,
                    title: offer.name,
                    price: offer.price,
                    optionTypeLabel: getOfferTypeByTag(offer.tags?.[0]),
                    type: 'SUBSCRIPTION',
                    image: offer.poster,
                    duration: translate('SUBSCRIPTION_PERIOD_MONTH'),
                    rawData: offer,
                    onClick: () => {
                        selectOffer(offer);
                    },
                }))}
            />
        );
    };

    const getConfirmOfferScreen = () => (
        <SubscriptionConfirmation
            onBack={goBack}
            canGoBack={canGoBack}
            onClose={() => {
                if (subscriptionCallback?.onCancel) {
                    subscriptionCallback.onCancel();
                }
                cancel();
            }}
            onConfirm={goToEnterPin}
            selectedOffer={selectedOffer}
            loading={isOrdering}
        />
    );

    const getGenericError = () => {
        const genericError = subscriptionOfferError;

        if (genericError) {
            const code = parseInt(genericError?.response?.status, 10);

            return (
                <AlertDialog
                    title={translate('ERR_GENERIC_TITLE')}
                    bodyText={translate('ERR_GENERIC_BODY')}
                    hintText={`${translate('ERR_CODE')} ${code}`}
                    buttons={[
                        {
                            text: translate('CLOSE_BUTTON'),
                            onClick: () => {
                                if (subscriptionCallback?.onCancel) {
                                    subscriptionCallback.onCancel();
                                }
                                cancel();
                            },
                        },
                    ]}
                />
            );
        }

        return null;
    };

    const getErrorAlert = () => {
        const error = orderError;

        if (error) {
            const code = parseInt(error?.response?.status, 10);

            return (
                <AlertDialog
                    title={translate('ERR_SUBSCRIBE_GENERIC_TITLE')}
                    bodyText={translate('ERR_SUBSCRIBE_GENERIC_BODY')}
                    hintText={`${translate('ERR_CODE')} ${code}`}
                    buttons={[
                        {
                            text: translate('OK_BUTTON'),
                            onClick: () => {
                                if (subscriptionCallback?.onCancel) {
                                    subscriptionCallback.onCancel();
                                }
                                cancel();
                            },
                        },
                    ]}
                />
            );
        }

        return getGenericError();
    };

    const getScreen = () => {
        if (!step) {
            return null;
        }

        if (step === FlowStep.UPSELL_MESSAGE) {
            return getUpsellMessageScreen();
        }

        if (pendingFlow && !subscriptionOfferError) {
            if (step === FlowStep.SELECT_OFFER) {
                return getSelectOfferScreen();
            }

            if (step === FlowStep.CONFIRM_OFFER) {
                return getConfirmOfferScreen();
            }

            if (step === FlowStep.ENTER_PIN) {
                return null;
            }

            if (step === FlowStep.PROCESSING) {
                return getActivationScreen();
            }

            if (step === FlowStep.ACTIVATION_DELAYED) {
                return getActivationDelayedAlert();
            }
        }

        return getErrorAlert();
    };

    return getScreen;
};

export const useSubscriptionChangedObserver = (packageIds: string[], callback: () => void, deps: any[] = []) => {
    const { addSubscriptionChangedListener, removeSubscriptionChangedListener } = useSubscription();

    const onChanged = (data: SubscriptionValidatorInfo) => {
        if (data && packageIds.length && packageIds.includes(data.packageId)) {
            callback();
        }
    };

    useEffect(() => {
        addSubscriptionChangedListener(onChanged);
        return () => removeSubscriptionChangedListener(onChanged);
    }, deps);
};
